import { useEffect, useState } from "react";
import { CalendarIcon, Loader2, Trash2, ImagePlus, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { staffApi, errMsg, getRole } from "@/lib/api";
import { CATEGORIES, TIMES } from "@/components/staff/categories";

const EMPTY = { title: "", category: "event", date: "", start_time: "", end_time: "", location: "", notes: "", description: "", assigned_to: "", is_public: false, community_id: "", minister_id: "", minister_name: "", request_id: "" };
const FIELDS = Object.keys(EMPTY);
const NONE = "__none";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>;

const TimeSelect = ({ value, onChange, testId }) => (
  <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="--" /></SelectTrigger>
    <SelectContent className="max-h-64 bg-white"><SelectItem value={NONE}>No time</SelectItem>
      {TIMES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const EventDialog = ({ state, staff, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [communities, setCommunities] = useState([]);
  useEffect(() => { if (getRole() === "admin") staffApi().get("/communities").then((r) => setCommunities(r.data)).catch(() => {}); }, []);
  const editing = state.event;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (state.open) {
      setPhoto(null);
      setForm(editing ? { ...EMPTY, ...editing } : { ...EMPTY, date: format(state.date || new Date(), "yyyy-MM-dd") });
    }
  }, [state, editing]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please add a title");
    if (photo && (!["image/jpeg", "image/png", "image/webp"].includes(photo.type) || photo.size > 10 * 1024 * 1024)) {
      return toast.error("Photo must be a JPG, PNG or WebP under 10 MB");
    }
    setBusy(true);
    let savedId = editing?.id;
    try {
      const body = Object.fromEntries(FIELDS.map((k) => [k, form[k] ?? EMPTY[k]]));
      const { data } = editing ? await staffApi().put(`/events/${editing.id}`, body) : await staffApi().post("/events", body);
      savedId = data.id;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await staffApi().post(`/events/${data.id}/photo`, fd);
      }
      toast.success(editing ? "Schedule updated — team notified" : "Schedule added — team notified");
      onSaved(); onClose();
    } catch (e) {
      toast.error(errMsg(e));
      if (savedId && !editing) { toast.info("The schedule was saved; only the photo failed. You can add it by editing the schedule."); onSaved(); onClose(); }
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this schedule?")) return;
    setBusy(true);
    try { await staffApi().delete(`/events/${editing.id}`); toast.success("Schedule removed"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl bg-white sm:max-w-lg" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{editing ? "Edit schedule" : "New schedule"}</DialogTitle>
          <DialogDescription>Events, weddings, dedications, funerals and pastoral care.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Title"><Input value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. Santos–Reyes Wedding" data-testid="event-title-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type">
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="event-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(CATEGORIES).filter(([k]) => k !== "worship").map(([k, c]) => <SelectItem key={k} value={k} data-testid={`event-category-${k}`}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal" data-testid="event-date-button">
                    <CalendarIcon className="mr-2 h-4 w-4" />{form.date ? format(parseISO(form.date), "MMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto bg-white p-0" align="start">
                  <Calendar mode="single" selected={form.date ? parseISO(form.date) : undefined} onSelect={(d) => d && set("date")(format(d, "yyyy-MM-dd"))} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Start"><TimeSelect value={form.start_time} onChange={set("start_time")} testId="event-start-select" /></Field>
            <Field label="End"><TimeSelect value={form.end_time} onChange={set("end_time")} testId="event-end-select" /></Field>
          </div>
          <Field label="Community (optional)">
            <Select value={form.community_id || NONE} onValueChange={(v) => set("community_id")(v === NONE ? "" : v)}>
              <SelectTrigger data-testid="event-community-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value={NONE}>Not a community event</SelectItem>
                {communities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Assigned pastor / staff">
            <Select value={form.assigned_to || NONE} onValueChange={(v) => set("assigned_to")(v === NONE ? "" : v)}>
              <SelectTrigger data-testid="event-assigned-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value={NONE}>Unassigned</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Location"><Input value={form.location} onChange={(e) => set("location")(e.target.value)} data-testid="event-location-input" /></Field>
          {form.minister_name && <p className="rounded-xl bg-[#EBF3FA] px-3 py-2 text-sm text-[#0A3A6B]" data-testid="event-minister-tag">Ministering pastor: <b>{form.minister_name}</b> (set from Pastoral Requests)</p>}
          <Field label="Internal notes (staff only)"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="event-notes-input" /></Field>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <div><p className="text-sm font-medium text-slate-800">Show as public announcement</p><p className="text-xs text-slate-500">Appears on the website's "What's coming up"</p></div>
            <Switch checked={form.is_public} onCheckedChange={set("is_public")} data-testid="event-public-switch" />
          </div>
          {form.is_public && (
            <div className="space-y-4 rounded-xl border border-dashed border-slate-200 p-4" data-testid="event-page-fields">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]">Public event page</p>
              <Field label="Description for visitors"><Textarea rows={4} value={form.description} onChange={(e) => set("description")(e.target.value)} placeholder="What's happening, who it's for, what to bring..." data-testid="event-description-input" /></Field>
              <label className="flex cursor-pointer items-center gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600 hover:bg-[#EBF3FA]">
                <ImagePlus className="h-5 w-5 shrink-0 text-[#1D64B4]" />
                <span className="truncate">{photo ? photo.name : editing?.image_path ? "Photo added — choose to replace" : "Add a photo (JPG, PNG, WebP)"}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="event-photo-input" />
              </label>
              {editing?.is_public && (
                <Link to={`/events/${editing.id}`} target="_blank" className="flex items-center gap-1 text-sm font-semibold text-[#0A3A6B] hover:text-[#1D64B4]" data-testid="event-view-page-link">
                  View public page <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-2">
          {editing ? <Button variant="ghost" onClick={remove} disabled={busy} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid="event-delete-button"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button> : <span />}
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#0A3A6B] px-6 hover:bg-[#1D64B4]" data-testid="event-save-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
